import { useQuery, useInfiniteQuery, UseQueryOptions } from "@tanstack/react-query";
import moment from "moment";

import {
  ActivityDto,
  ActivityQueryFiltersDto,
  ExpiredTicketDigestDto,
  ExpiredTicketWithReminderDigestDto,
  FindAllTicketsQueryFilters,
  TicketDto,
  TicketFilterDto,
  TicketFlatFilterDto,
  TicketsByDateDto,
} from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { ticketKeys } from "./ticket.keys";

// =============================================================================
// Types
// =============================================================================

export type ExpiredTicketType = ExpiredTicketDigestDto | ExpiredTicketWithReminderDigestDto;

// =============================================================================
// Single Ticket Query
// =============================================================================

/**
 * Fetch a single ticket by ID
 */
export const useTicket = (ticketId?: number) => {
  const { tickets: ticketsApi } = useApi();
  
  return useQuery<TicketDto, Error>({
    queryKey: ticketId ? ticketKeys.ticketById.all(ticketId) : [ticketKeys.ticketById.scope],
    queryFn: async () => {
      if (!ticketId) {
        throw new Error("Ticket ID ist erforderlich");
      }
      const response = await ticketsApi.getTicketById(ticketId);
      return response;
    },
    staleTime: 0,
    enabled: !!ticketId,
  });
};

// =============================================================================
// Tickets List Query (Infinite)
// =============================================================================

interface UseTicketsOptions {
  filters: FindAllTicketsQueryFilters;
  enabled?: boolean;
}

/**
 * Fetch paginated tickets with filters (infinite query)
 */
export const useTickets = ({ filters, enabled = false }: UseTicketsOptions) => {

  // console.debug("useTickets called with filters:", filters);
  const { tickets: ticketsApi } = useApi();
  const limit = filters.limit ?? 20;
  
  return useInfiniteQuery<TicketDto[], Error>({
    queryKey: ticketKeys.tickets.filtered(filters),
    queryFn: ({ pageParam = 1 }) =>
      ticketsApi.getAllTickets({ ...filters, page: pageParam as number }),
    staleTime: moment.duration(1, "hour").asMilliseconds(),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length >= limit ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: enabled,
  });
};

// =============================================================================
// Ticket Filters Queries
// =============================================================================

/**
 * Fetch ticket filter options (grouped format)
 */
export const useTicketFilter = () => {
  const { tickets: ticketsApi } = useApi();

  return useQuery<TicketFilterDto[], Error>({
    queryKey: ticketKeys.ticketFilters.all(),
    queryFn: () => ticketsApi.getTicketFilters(),
    staleTime: moment.duration(1, "hour").asMilliseconds(),
  });
};

/**
 * Fetch ticket filter options (flat format)
 */
export const useTicketFiltersFlat = () => {
  const { tickets: ticketsApi } = useApi();

  return useQuery<TicketFlatFilterDto, Error>({
    queryKey: ticketKeys.ticketFilters.flatAll(),
    queryFn: () => ticketsApi.getTicketFlatFilters(),
    staleTime: moment.duration(1, "hour").asMilliseconds(),
  });
};

// =============================================================================
// Dashboard Digest Queries
// =============================================================================

interface UseExpiredTicketsProps {
  enabled?: boolean;
  refetchInterval?: number;
  digestType?: "team" | "personal" | "rx";
  userId?: string;
  locationId: string;
}

/**
 * Fetch expired tickets for dashboard digests
 * Used by TeamDigest, PersonalDigest, and RxOrdersDigest
 */
export const useExpiredTickets = ({
  enabled = true,
  refetchInterval,
  digestType,
  userId,
  locationId,
}: UseExpiredTicketsProps) => {
  const { tickets: ticketsApi } = useApi();

  return useQuery<ExpiredTicketType[], Error>({
    queryKey: ticketKeys.expiredTickets.byDigestType(digestType, userId, locationId),
    queryFn: () => ticketsApi.getExpiredTickets(locationId, digestType, userId),
    staleTime: moment.duration(5, "minutes").asMilliseconds(),
    refetchInterval: refetchInterval || moment.duration(10, "minutes").asMilliseconds(),
    enabled: enabled && (digestType !== "personal" || !!userId) && !!locationId,
  });
};

/**
 * Fetch tickets for a specific date (dashboard digests)
 * Used by TeamDigest, PersonalDigest, and RxOrdersDigest
 */
export const useTicketsByDate = (
  date: string,
  locationId: string,
  digestType?: "team" | "personal" | "rx",
  userId?: string,
  options?: Omit<UseQueryOptions<TicketsByDateDto[], Error>, "queryKey" | "queryFn">
) => {
  const { tickets: ticketsApi } = useApi();

  return useQuery({
    queryKey: ticketKeys.ticketsByDate.byDateAndDigestType(date, digestType, userId, locationId),
    queryFn: () => ticketsApi.getTicketsByDate(date, locationId, digestType, userId),
    enabled: !!date && (digestType !== "personal" || !!userId) && !!locationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes
    ...options,
  });
};

// =============================================================================
// Ticket Activities Query (Infinite)
// =============================================================================

/**
 * Fetch paginated ticket activities (comments, reminders, etc.)
 */
export const useFetchTicketActivities = (filters: ActivityQueryFiltersDto) => {
  const { tickets: ticketsApi } = useApi();
  
  return useInfiniteQuery<ActivityDto[], Error>({
    queryKey: ticketKeys.activities.byTicketFiltered(filters.ticketId!, filters),
    queryFn: ({ pageParam = 1 }) => {
      const { limit, ticketId, type } = filters;
      return ticketsApi.getAllTicketActivities(limit, pageParam as number, "ASC", type, ticketId);
    },
    staleTime: moment.duration(1, "hour").asMilliseconds(),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!filters.ticketId,
  });
};
